import React, { FC, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { AccountContentContainer } from './style';
import AddOnCard from 'components/AddOnCard';

import StyledButton from 'components/Button';
import { BookingAddOn } from 'states/OptionStates/atoms';
import { useGetBookingAddOns } from 'customHooks/useGetBookingAddOns';
import { useLoadTrips } from 'customHooks/useLoadTrips';
import moment from 'moment';
import Checkbox from 'components/Checkbox'
import { colors } from 'constants/colors';
import { useChangeAddons } from 'customHooks/useChangeAddons';

import { smoothNavigate } from "components/SmoothNavigate";

import { calcModifyAddonsPreview } from "utils/calcModifyAddonsPreview";

const AddOnsContent: FC = () => {



  const history = useHistory()
  const debug = false;

  const { trips, loading: tripsLoading } = useLoadTrips()

  const { handleChangeAddons } = useChangeAddons()

  const bookingAddOns = useGetBookingAddOns()

  if (debug) {
    console.log("(Modify Add-ons)  bookingAddOns:", bookingAddOns);
  }

  const { groupId } = useParams<{ groupId: string }>()
  const { bookingId } = useParams<{ bookingId: string }>()
  const trip = trips[groupId]

  const tripAddOns = React.useMemo(() => trip?.bookings
    ?.filter(booking => booking.bookingId === bookingId)
    ?.flatMap(booking =>
      booking.addons?.map(addon => ({
        id: addon.bookingAddOn?.addOnId ?? addon.bookingAddOn?.id,
        quantity: addon.quantity,
        // NEW: use addon.numberOfNights if backend provided it, otherwise fallback to booking nights
        numberOfNights:
          (addon as any)?.numberOfNights ??
          booking.roomBookings?.[0]?.numberOfNights ??
          1,
      })) ?? []
    ) ?? [], [trip, bookingId]);

    if (debug) {
      console.log ('(Modify Add-ons) trip:',trip);
    }

  const currentBooking = trip?.bookings.find(booking => booking.bookingId === bookingId) || null;
  const currentBookingNumberOfNights = currentBooking?.roomBookings?.[0]?.numberOfNights;

  const BREAKFAST_ID = "63f6e8f8-8095-4c79-8d92-ce5812db4f15";
  const PARKING_ID = "fd67a4cb-667a-4ba9-bec4-379cb430682a";
  const EARLY_CHECK_IN_ID = "4c773a2c-0d0a-44ba-8680-d34244fea4d5";

  const bookingNights =
    currentBooking?.roomBookings?.[0]?.numberOfNights ?? 1;

  const shouldShowNightsCounter = bookingNights > 1;


 // Show Early Check-in block?

 const isTodayCheckIn = currentBooking?.checkInDate
 ? moment(currentBooking.checkInDate).isSame(moment(), 'day')
 : false;

const isAfterEarlyCheckInCutoff =
 moment().isSameOrAfter(moment().hour(15).minute(0).second(0));

const shouldHideEarlyCheckIn =
 isTodayCheckIn && isAfterEarlyCheckInCutoff;

// Show Early Check-in block?



  const [tripAddOnsUpdated, setTripAddOnsUpdated] = useState<any[]>([]);
  // addons data updated by user

  const [tripAddOnsInitialAddons, setTripAddOnsInitialAddons] = useState<any[]>([]);
  // addons data that came form server

  
  const [addOnsChangedByUser, setAddOnsChangedByUser] = useState(false)


  const charges = trip?.bills?.[0]?.charges ?? [];

  // Hide breakfast widget if support manually removed any breakfast charge
  const hasDeletedBreakfast = charges.some(
    (c: any) => typeof c?.description === "string" && c.description.includes("Breakfast") && c.isDeleted
  );

  // Hide parking widget if support manually removed any parking charge
  const hasDeletedParking = charges.some(
    (c: any) => typeof c?.description === "string" && c.description.includes("Parking") && c.isDeleted
  );




  useEffect(() => {

    if (!addOnsChangedByUser) {

      // Get new addons
      const updatedAddOns = [
        // addons from booking (already include numberOfNights from step #1)
        ...tripAddOns.map(a => ({
          ...a,
          numberOfNights:
            a.numberOfNights ??
            currentBookingNumberOfNights ??
            1,
        })),

        // addons that exist in list but not yet selected -> default nights = booking nights
        ...bookingAddOns
          .filter(bookingAddOn => {
            // hide breakfast/parking widgets if deleted charges exist
            if (bookingAddOn.addOnId === BREAKFAST_ID && hasDeletedBreakfast) return false;
            if (bookingAddOn.addOnId === PARKING_ID && hasDeletedParking) return false;

            return !tripAddOns.some(addon => addon.id === bookingAddOn.addOnId);
          })
          .map(bookingAddOn => ({
            id: bookingAddOn.addOnId,
            quantity: 0,
            numberOfNights: currentBookingNumberOfNights ?? 1,
          })),
      ];


      // Update if it's really new addons 
      if (JSON.stringify(updatedAddOns) !== JSON.stringify(tripAddOnsInitialAddons)) {
        setTripAddOnsInitialAddons(updatedAddOns);
      }

      if (JSON.stringify(updatedAddOns) !== JSON.stringify(tripAddOnsUpdated)) {
        setTripAddOnsUpdated(updatedAddOns);
      }
      setAddOnsChangedByUser(false);
    }

  }, [tripAddOns, bookingAddOns, currentBookingNumberOfNights, hasDeletedBreakfast, hasDeletedParking, addOnsChangedByUser, tripAddOnsInitialAddons, tripAddOnsUpdated]);

  const [applyThisChangeToAllRooms, setApplyThisChangeToAllRooms] = useState(false);

  const hasActualChanges = React.useMemo(() => {
    if (!tripAddOnsInitialAddons.length || !tripAddOnsUpdated.length) return false;
  
    const normalize = (arr: any[]) =>
      [...arr]
        .map((a) => ({
          id: String(a.id),
          quantity: Number(a.quantity ?? 0),
          numberOfNights: Number(a.numberOfNights ?? bookingNights ?? 1),
        }))
        .sort((a, b) => a.id.localeCompare(b.id));
  
    return JSON.stringify(normalize(tripAddOnsInitialAddons)) !== JSON.stringify(normalize(tripAddOnsUpdated));
  }, [tripAddOnsInitialAddons, tripAddOnsUpdated, bookingNights]);

  const isAddonsDataReady =
  !tripsLoading &&
  !!trip &&
  !!currentBooking &&
  bookingAddOns.length > 0 &&
  tripAddOnsUpdated.length > 0;

  const preview = React.useMemo(() => {
    return calcModifyAddonsPreview({
      trip,
      bookingId: String(bookingId || ""),
      tripAddOnsUpdated,
      bookingAddOns,
      bookingNights,
      applyToAll: applyThisChangeToAllRooms,
      hasUserChangedAddons: hasActualChanges,
    });
  }, [
    trip,
    bookingId,
    tripAddOnsUpdated,
    bookingAddOns,
    bookingNights,
    applyThisChangeToAllRooms,
    hasActualChanges,
  ]);
  
  const addOnsChanged = !!preview;

  const money = (n: number) => `$${Number(n || 0).toFixed(2)}`;

  const totalBookingsCount = trip?.bookings?.length ?? 0;
  const shouldShowApplyToAll = totalBookingsCount > 1;
  









  const setChecked = (addOnId: string) => {
    setAddOnsChangedByUser(true);
    if (debug) {
      console.log('(Modify Add-ons) Adon ID to change:', addOnId);
    }
    setTripAddOnsUpdated(prevAddOns =>
      prevAddOns.map(addon => {
        console.log('Current addon:', addon);
        return addon.id === addOnId
          ? { ...addon, quantity: (addon.quantity ?? 0) > 0 ? 0 : 1 }
          : addon;
      })
    );
  };



  const setQuantity = (newQuantity: number, addOnId: string) => {
    setAddOnsChangedByUser(true);
    setTripAddOnsUpdated(prevAddOns =>
      prevAddOns.map(addon =>
        addon.id === addOnId ? { ...addon, quantity: newQuantity } : addon
      )
    );
  };


  const setAddonNumberOfNights = (newNights: number, addOnId: string) => {
    setAddOnsChangedByUser(true);

    const max = bookingNights ?? 1;
    const clamped = Math.max(1, Math.min(newNights, max));

    setTripAddOnsUpdated(prevAddOns =>
      prevAddOns.map(addon =>
        addon.id === addOnId
          ? { ...addon, numberOfNights: clamped }
          : addon
      )
    );
  };

  const cancelChange = () => {
    setAddOnsChangedByUser(false);
    smoothNavigate(history, `/account/bookings/add-ons/${groupId}/${bookingId}`)
  }

  const saveUpdatedAddons = () => {

    // RD-5 
    const hiddenAddonIds = new Set<string>();
    if (hasDeletedBreakfast) hiddenAddonIds.add(BREAKFAST_ID);
    if (hasDeletedParking) hiddenAddonIds.add(PARKING_ID);

    const addonsArray = !applyThisChangeToAllRooms
      ? tripAddOnsUpdated
        .filter(addon => !hiddenAddonIds.has(addon.id))
        .filter(addon => !(addon.quantity === 0 && !tripAddOns.some(t => t.id === addon.id)))
        .map(addon => ({
          bookingId,
          id: addon.id,
          quantity: addon.quantity,
          numberOfNights: addon.numberOfNights ?? bookingNights ?? 1,
        }))
      : (trip?.bookings ?? []).flatMap(booking => {
        if (debug) {
          console.log("(Modify Add-ons) booking for addons: ", booking);
        }
        return tripAddOnsUpdated
        .filter(addon => !hiddenAddonIds.has(addon.id))
        .filter(updatedAddon => {
            const existingAddon = booking.addons?.find(addonFromBooking => addonFromBooking.bookingAddOn?.id === updatedAddon.id);
            if (debug) {
              console.log("(Modify Add-ons) existingAddon: ", existingAddon);
              console.log("(Modify Add-ons) updatedAddon: ", updatedAddon);

            }
            return (existingAddon && existingAddon.quantity !== updatedAddon.quantity) || (!existingAddon && updatedAddon.quantity !== 0);
          })
          .map(addon => ({
            bookingId: booking.bookingId,
            id: addon.id,
            quantity: addon.quantity,
            numberOfNights: addon.numberOfNights ?? (booking.roomBookings?.[0]?.numberOfNights ?? 1),
          }));
      });

    if (debug) {
      console.log("(Modify Add-ons) addonsArray to send =", addonsArray);
    }

    handleChangeAddons(addonsArray);
    smoothNavigate(history, `/account/bookings/${groupId}`)
  };


  // set addons order:

  const ORDERED_ADDON_IDS = [
    "63f6e8f8-8095-4c79-8d92-ce5812db4f15",
    "fd67a4cb-667a-4ba9-bec4-379cb430682a",
    "c5032166-bd7f-4a8a-bc4b-81d5c7fc5ff5",
    "4c773a2c-0d0a-44ba-8680-d34244fea4d5",
  ];

  

  const optionArray = !isAddonsDataReady
  ? null
  : [...bookingAddOns]
  .sort((a, b) => {
      const aIndex = ORDERED_ADDON_IDS.indexOf(a.addOnId);
      const bIndex = ORDERED_ADDON_IDS.indexOf(b.addOnId);

      const safeA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
      const safeB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;

      return safeA - safeB;
    })
    .map((addOn: BookingAddOn) => {
      const foundOption =
        tripAddOnsUpdated.find(option => option?.id === addOn.addOnId) || { quantity: 0, numberOfNights: bookingNights };

      const quantity: number = foundOption.quantity ?? 0;
      const numberOfNights: number = foundOption.numberOfNights ?? bookingNights;


      const oneTimePricedChecked = quantity > 0;


      // Build AddOnCard props in one place to avoid prop overrides and legacy logic leaks.
      const isBreakfast = addOn.addOnId === BREAKFAST_ID;
      const isParking = addOn.addOnId === PARKING_ID;
      const isEarlyCheckIn = addOn.addOnId === EARLY_CHECK_IN_ID;

      // don't show if hasDeletedBreakfast or hasDeletedParking
      if (isBreakfast && hasDeletedBreakfast) return null;
      if (isParking && hasDeletedParking) return null;
      
      // don't show if booking is today and time > 15:00
      if (isEarlyCheckIn && shouldHideEarlyCheckIn) return null;

      const hasNightsCounter = isBreakfast || isParking;

      const safeQty = typeof quantity === "number" ? quantity : 0;
      const safeNights = typeof numberOfNights === "number" ? numberOfNights : 0;
      const safePrice = typeof addOn.pricePerNight === "number" ? addOn.pricePerNight : 0;

      const formatMoney = (value: number) => `$${value.toFixed(2)}`;

      const buildNightsExplanation = (label: string) => {
        const total = safePrice * safeQty * safeNights;
      
        return (
          <span>
            {formatMoney(safePrice)} / {label} @ {safeNights}{" "}
            {safeNights > 1 ? "Nights" : "Night"} = <strong>{formatMoney(total)}</strong>
          </span>
        );
      };

      const nightsProps = hasNightsCounter
        ? {
          valueText: isBreakfast ? (quantity > 1 ? "People" : "Person") : (quantity > 1 ? "Cars" : "Car"),
          numberOfNightsText: (numberOfNights > 1 ? "Nights" : "Night"),
          ...(shouldShowNightsCounter
            ? {
              numberOfNights,
              setNumberOfNights: (newNights: number) =>
                setAddonNumberOfNights(newNights, addOn.addOnId),
            }
            : {}),
            explanation: isBreakfast
            ? buildNightsExplanation(quantity > 1 ? "person" : "person")
            : buildNightsExplanation(quantity > 1 ? "car" : "car"),
        }
        : {};

      // 1) Quantity props: breakfast/parking still need `value` + `setValue`,
      // but MUST NOT override their valueText.

      const quantityProps = addOn.isOneTimePriced
        ? {
          checked: oneTimePricedChecked,
          setChecked: () => setChecked(addOn.addOnId),
          explanation: "per room, not including taxes.",
        }
        : {
          value: quantity,
          setValue: (newQuantity: number) => setQuantity(newQuantity, addOn.addOnId),
          ...(hasNightsCounter ? {} : { valueText: "Qty" }),
        };

      const attributesTextProps = isBreakfast
        ? {
          attributesText: "Please select the number of people staying in this room which would like a breakfast included in their stay.",
          showItemPrice: false,
          explanationPositionLeft: false,

        }
        : isParking
          ? {
            attributesText: "Please select the number of cars for this room.",
            showItemPrice: false,
            explanationPositionLeft: false,
          }
          : {
            showItemPrice: true,
            explanationPositionLeft: true,
          };


      return (
        <AddOnCard
          key={addOn.addOnId}
          title={addOn.label}
          attributes={addOn.description}
          isOneTimePriced={addOn.isOneTimePriced}
          price={addOn.pricePerNight}
          maxNumberOfNights={bookingNights}
          {...attributesTextProps}
          {...nightsProps}
          {...quantityProps}
        />
      );

    });






  return (
    <AccountContentContainer>


      <div className="addonsContainer">
      {isAddonsDataReady ? optionArray : "Loading Add-ons data..."}
      </div>
      {addOnsChanged && preview ? (
  <>
    {shouldShowApplyToAll ? (
      <div className="addonsContainerApplyToAll">
        <Checkbox
          className="accountCheckbox"
          checked={applyThisChangeToAllRooms}
          borderColor={colors.signInBorderColor}
          label="Apply this change to all rooms"
          onChange={() => {
            setApplyThisChangeToAllRooms(!applyThisChangeToAllRooms);
          }}
          checkboxScale={1.2}
        />
      </div>
    ) : null}

    <div className="addonsChanges">
      <div className="addonsOverview">OVERVIEW</div>

      {preview.lines.map((line) => {
        const isRed = !!line.red;
        const amount = Number(line.amount || 0);

        return (
          <div className="addonsOverviewLine" key={line.key}>
            <div className={`addonsOverviewTitle addonsOverviewTitleWithPadding ${isRed ? "priceRemove" : ""}`}>
              <div className="addonNameAndPrice">{line.title}</div>
            </div>

            <div className="addonsOverviewDots">
              <div className="dashedLine"></div>
            </div>

            <div className={`addonsOverviewNumber ${isRed ? "priceRemove" : ""}`}>
              {amount < 0 ? "-" : ""}{money(Math.abs(amount))}
            </div>
          </div>
        );
      })}

      <div className="addonsOverviewTotal">
        <div className="addonsOverviewTotalTitle">YOUR NEW TOTAL</div>
        <div className="addonsOverviewDots"></div>
        <div className="addonsOverviewNumber">
          {money(Number(preview.total || 0))}
        </div>
      </div>

      <div className="buttonContainer">
        <div
          className="cancelBt"
          onClick={() => {
            cancelChange();
          }}
        >
          Cancel
        </div>

        <StyledButton
          width="98px"
          height="48px"
          onClick={() => {
            saveUpdatedAddons();
          }}
        >
          Submit
        </StyledButton>
      </div>
    </div>
  </>
) : null}
    </AccountContentContainer>
  );
};

export default AddOnsContent;